import { type RefObject, useEffect, useRef } from "react";
import { type BlockerFunction, NavigationType, parsePath, useBlocker, useNavigate } from "react-router";

type UnsavedProposalNavigationGuardOptions = {
  /**
   * Answers whether a navigation has to wait for the "Discard changes?" prompt. The page owns this,
   * because only the page knows what "dirty" means for it.
   */
  shouldBlockNavigation: BlockerFunction;
  /**
   * Asks the user what to do, saves or discards, and calls `continueNavigation` once the page is
   * ready to be left. It answers `true` when it released the navigation, and `false` when the user
   * cancelled or the save failed. Not calling `continueNavigation` is what keeps the user on the page.
   */
  onNavigateAway: (continueNavigation: () => void) => Promise<boolean>;
  /**
   * The path the page navigates to itself, which the guard lets through without asking
   * `shouldBlockNavigation`. Only its pathname is compared, so a page may write the whole path it
   * navigates to, query included. The guard writes the blocked destination here before it takes the
   * user there, so the page does not prompt a second time for the same click.
   */
  appDirectedNavigationPathRef: RefObject<string | null>;
};

/**
 * Blocks navigation away from a proposal editor that holds unsaved edits, and takes the user to the
 * route they asked for once they have answered the prompt.
 *
 * The prompt waits on a person, and the router does not wait with it. Completing ANY navigation
 * idles every blocker (React Router's `completeNavigation`), and the editors navigate themselves
 * while the prompt is open: a new draft's first save replaces `/proposals/drafts/create/...` with
 * `/proposals/drafts/edit/{id}/...`, and an active proposal moves to a successor checkpoint when the
 * other party advances the negotiation. A blocker held open across the prompt can therefore be
 * retired underneath it, and `proceed()` or `reset()` on a retired blocker throws "Invalid blocker
 * state transition". Before this guard, the page answered that by skipping `proceed()`, which kept
 * the router safe and silently dropped the route the user asked for (#5545).
 *
 * So the guard never holds a blocker across the prompt. It releases the blocked navigation at once,
 * while the blocker is certainly still blocked, remembers where it was going, and navigates there
 * itself when the page calls `continueNavigation`. Nothing the router does while the prompt is open
 * can then leave the guard driving a blocker that is gone.
 */
export default function useUnsavedProposalNavigationGuard({
  shouldBlockNavigation,
  onNavigateAway,
  appDirectedNavigationPathRef,
}: UnsavedProposalNavigationGuardOptions): void {
  const navigate = useNavigate();

  // The router calls this during a navigation, never during a render, so it reads the values of the
  // render that armed it. `historyAction` reaches the effect no other way: a `Blocker` carries the
  // destination but not how the user asked for it.
  const blockedHistoryActionRef = useRef<NavigationType>(NavigationType.Push);
  const blocker = useBlocker((navigationArguments) => {
    // A router location never carries its query in `pathname`, and the pages carry the query along on
    // their own moves (a Comments link, say). Comparing the whole directed path blocked the active
    // page's own move to a successor checkpoint whenever the URL had a query (review of #7070).
    const appDirectedPath = appDirectedNavigationPathRef.current;
    if (appDirectedPath !== null && parsePath(appDirectedPath).pathname === navigationArguments.nextLocation.pathname) {
      return false;
    }
    const shouldBlock = shouldBlockNavigation(navigationArguments);
    if (shouldBlock) blockedHistoryActionRef.current = navigationArguments.historyAction;
    return shouldBlock;
  });

  // Read through a ref so a new `onNavigateAway` identity cannot re-run the effect below and ask the
  // same question twice. The blocker instance is the only thing that effect reacts to.
  const onNavigateAwayRef = useRef(onNavigateAway);
  useEffect(() => {
    onNavigateAwayRef.current = onNavigateAway;
  });

  // Navigating after the page has gone would move the application from a component nobody looks at.
  // This flag is read long after every await, so the order of the unmount cleanups does not matter.
  const pageIsMountedRef = useRef(true);
  useEffect(() => {
    pageIsMountedRef.current = true;
    return () => {
      pageIsMountedRef.current = false;
    };
  }, []);

  // Counts the blocked navigations this guard has taken on. The navigation is released before the
  // prompt opens, so the user can ask to leave again while it is open; only the newest request is
  // carried out.
  const blockedNavigationCountRef = useRef(0);

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    const destination = blocker.location;
    // A blocked POP is the user going back or forward. Pushing the destination on top of the entry
    // they asked to leave would keep that entry behind them, so the entry is replaced instead.
    const destinationReplacesCurrentEntry = blockedHistoryActionRef.current === NavigationType.Pop;
    const blockedNavigationNumber = blockedNavigationCountRef.current + 1;
    blockedNavigationCountRef.current = blockedNavigationNumber;

    // Released here, in the commit that reported it blocked, before anything can navigate.
    blocker.reset();

    void onNavigateAwayRef.current(() => {
      if (!pageIsMountedRef.current) return;
      if (blockedNavigationNumber !== blockedNavigationCountRef.current) return;

      appDirectedNavigationPathRef.current = destination.pathname;
      navigate(
        { pathname: destination.pathname, search: destination.search, hash: destination.hash },
        { replace: destinationReplacesCurrentEntry, state: destination.state },
      );
    });
  }, [appDirectedNavigationPathRef, blocker, navigate]);
}
